<script lang="ts">
  import { Settings, X, Save } from 'lucide-svelte';
  import Button from '../ui/button.svelte';
  import Card from '../ui/card.svelte';
  import { filteredFlashcards, filteredCategories } from '../../stores/flashcards';
  import { _ } from 'svelte-i18n';

  interface Props {
    open: boolean;
    onClose: () => void;
    onStart: (questionCount: number, selectedCategories: string[]) => void;
  }

  let { open = false, onClose, onStart }: Props = $props();

  // Custom test configuration
  let selectedCategories = $state<string[]>([]);
  let questionCount = $state(10);

  // Available flashcards based on selected categories
  let availableFlashcards = $derived(() => {
    if (selectedCategories.length === 0) {
      return $filteredFlashcards;
    }
    return $filteredFlashcards.filter(card =>
      card.category && selectedCategories.includes(card.category.id)
    );
  });

  function toggleCategory(categoryId: string) {
    if (selectedCategories.includes(categoryId)) {
      selectedCategories = selectedCategories.filter(id => id !== categoryId);
    } else {
      selectedCategories = [...selectedCategories, categoryId];
    }
  }

  function selectAllCategories() {
    selectedCategories = $filteredCategories.map(cat => cat.id);
  }

  function deselectAllCategories() {
    selectedCategories = [];
  }

  function handleStart() {
    onStart(questionCount, selectedCategories);
  }

  // Reset state when dialog closes
  $effect(() => {
    if (!open) {
      selectedCategories = [];
      questionCount = 10;
    }
  });
</script>

{#if open}
  <div
    role="dialog"
    aria-modal="true"
    tabindex="0"
    class="fixed inset-0 z-[96] flex flex-col bg-background"
    onclick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    onkeydown={(e) => { if (e.key === 'Escape') onClose(); }}
  >
    <!-- Header -->
    <div class="flex items-center justify-between p-6 border-b border-border">
      <div class="flex items-center gap-3">
        <div class="p-2 bg-secondary/10 rounded-full">
          <Settings class="w-6 h-6 text-secondary" />
        </div>
        <h2 class="text-xl font-bold text-foreground">{$_('test.custom_test')}</h2>
      </div>
      <button onclick={onClose} class="btn btn-close" aria-label={$_('aria.close')}>
        <X class="w-5 h-5" />
      </button>
    </div>

    <!-- Content -->
    <div class="px-6 pb-24 overflow-y-auto">
      <div class="mx-auto max-w-md py-6 space-y-4">
        <!-- Number of Questions -->
        <div>
          <label for="question-count" class="form-label">
            {$_('test.number_of_questions')}: <strong>{questionCount}</strong>
          </label>
          <input
            id="question-count"
            type="range"
            min="10"
            max="100"
            step="5"
            bind:value={questionCount}
            class="w-full accent-primary"
          />
          <div class="flex justify-between text-xs text-muted-foreground mt-1">
            <span>10</span>
            <span>50</span>
            <span>100</span>
          </div>
        </div>

        <!-- Category Selection -->
        <Card class="widget-card tone-list">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="form-label">
                {$_('test.categories')} ({selectedCategories.length} {$_('test.selected')})
              </div>
              <div class="flex gap-2">
                <button
                  type="button"
                  onclick={selectAllCategories}
                  class="text-xs text-primary hover:underline"
                >
                  {$_('test.all')}
                </button>
                <button
                  type="button"
                  onclick={deselectAllCategories}
                  class="text-xs text-muted-foreground hover:underline"
                >
                  {$_('test.none')}
                </button>
              </div>
            </div>

            {#if $filteredCategories.length === 0}
              <p class="text-sm text-muted-foreground">{$_('test.no_categories_yet')}</p>
            {:else}
              <div class="space-y-2 max-h-64 overflow-y-scroll pr-2 dialog-scroll">
                {#each $filteredCategories as category}
                  {@const isSelected = selectedCategories.includes(category.id)}
                  {@const cardCount = $filteredFlashcards.filter(c => c.category?.id === category.id).length}
                  <button
                    type="button"
                    onclick={() => toggleCategory(category.id)}
                    class="w-full flex items-center justify-between p-3 rounded-xl border-2 transition {isSelected
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'}"
                  >
                    <div class="flex items-center gap-3">
                      <div
                        class="w-5 h-5 rounded border-2 flex items-center justify-center transition {isSelected
                          ? 'border-primary bg-primary'
                          : 'border-muted-foreground'}"
                      >
                        {#if isSelected}
                          <svg class="w-3 h-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                          </svg>
                        {/if}
                      </div>
                      <span class="font-medium">{category.name}</span>
                    </div>
                    <span class="text-sm text-muted-foreground">{cardCount} {$_('test.flashcards')}</span>
                  </button>
                {/each}
              </div>
            {/if}
          </div>
        </Card>

        <!-- Available Flashcards Info -->
        <Card class="p-4 bg-muted rounded-xl">
          <p class="text-sm text-muted-foreground">
            {$_('test.available_flashcards')}: <strong class="text-foreground">{availableFlashcards().length}</strong>
          </p>
          {#if selectedCategories.length === 0}
            <p class="text-xs text-muted-foreground mt-1">
              {$_('test.all_categories')}
            </p>
          {/if}
        </Card>
      </div>
    </div>

    <!-- Footer -->
    <div class="p-6 border-t border-border flex gap-3">
      <Button variant="outline" onclick={onClose} class="btn btn-secondary flex-1">
        {$_('profile.save.cancel')}
      </Button>
      <Button
        onclick={handleStart}
        class="btn btn-primary flex-1"
        disabled={availableFlashcards().length < questionCount}
      >
        <Save class="w-4 h-4 mr-2" />
        {$_('profile.save.save')}
      </Button>
    </div>
  </div>
{/if}
